import { type CoverageArea } from '@apps/web/features/coverage/model/area_model';
import {
    type CoverageFilters,
    type CoverageRow,
    EMPTY_FILTERS,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import {
    buildFacetOptions,
    type FacetOptions,
    filterRows,
} from '@apps/web/features/coverage/model/filter_model';
import {
    buildCoverage,
    type CoverageBucket,
} from '@apps/web/features/coverage/model/rollup_model';
import { type WorkforceAvailabilityResponse } from '@libraries/typescript/api_client';

export type CoverageView = 'grid' | 'coverage' | 'map';

export type CoverageSearchState = CoverageFilters & {
    readonly view: CoverageView;
    readonly week: string | undefined;
    readonly area: CoverageArea;
    readonly includeInactive: boolean;
    readonly plotClients: boolean;
};

export type CoverageSearchUpdate = Partial<CoverageSearchState>;

export type CoverageKpis = {
    readonly shownCount: number;
    readonly rosterCount: number;
    readonly noAvailabilityCount: number;
};

export type CoverageDashboardData = {
    readonly rows: Array<CoverageRow>;
    readonly facetOptions: FacetOptions;
    readonly kpis: CoverageKpis;
    readonly coverage: Array<CoverageBucket>;
    readonly visibleCaregiverIds: ReadonlySet<string>;
    readonly workWeekStartDay: WorkWeekStartDay;
    readonly hasAnyCaregivers: boolean;
    readonly hasAnyAvailabilityData: boolean;
    readonly filtersActive: boolean;
};

export function deriveCoverageDashboardData(
    data: WorkforceAvailabilityResponse | undefined,
    search: CoverageSearchState,
): CoverageDashboardData {
    const allRows = data?.rows ?? [];
    const workWeekStartDay = data?.work_week_start_day ?? 'monday';
    const rows = filterRows(allRows, search);
    const withoutCellFilters = filterRows(allRows, {
        ...search,
        days: [],
        dayParts: [],
        availability: 'any',
    });
    const coverageRows = filterRows(allRows, {
        ...search,
        days: [],
        dayParts: [],
        matchMode: 'every',
    });
    const filtersActive =
        search.search !== '' ||
        search.offices.length > 0 ||
        search.cities.length > 0 ||
        search.zips.length > 0 ||
        search.days.length > 0 ||
        search.dayParts.length > 0 ||
        search.tags.length > 0 ||
        search.employmentTypes.length > 0 ||
        search.availability !== EMPTY_FILTERS.availability;

    return {
        rows,
        workWeekStartDay,
        facetOptions: buildFacetOptions(allRows),
        kpis: {
            shownCount: rows.length,
            rosterCount: data?.total ?? 0,
            noAvailabilityCount: withoutCellFilters.filter(
                (row) => !row.has_availability_data,
            ).length,
        },
        coverage: buildCoverage(coverageRows, search.area, workWeekStartDay),
        visibleCaregiverIds: new Set(rows.map((row) => row.caregiver_id)),
        hasAnyCaregivers: (data?.total ?? 0) > 0,
        hasAnyAvailabilityData: allRows.some(
            (row) => row.has_availability_data,
        ),
        filtersActive,
    };
}
